import { shallowRef } from "vue";

export const PRODUCTION_ENVS = ["staging", "platform"] as const;
export const TEST_STAGING_ENV_MODE = "test";
export const TEST_DEV_ENV_MODE = "test-dev";
export const DEV_ENV_MODE = "development";

export const FLAG_NAMES = [
  "DASHBOARD_OVERVIEW_CUSTOMER",
  "CUSTOMERS_TABLE_TYPE_COLUMN",
  "SEG_CHECKBOX",
  "INCIDENTS_V3_CATEGORIES",
  "MSP_OVERVIEW_CHART",
  "LOGOUT_WITH_REDIRECT",
  "DASHBOARD_EMAIL_ARCHIVING_CARD",
  "CONNECTIONS_V2",
  "PHS_USER_SUMMARY_CSV_EXPORT",
  "CUSTOMER_CREATED_TOAST_V2",
  "QUARANTINE_CONFIGURATION",
  "GEOBLOCKING_CONFIG_PAGE",
  "AUTH0_LOGIN",
  "HARNESS_DEMO_FLAG",
  "BLOCK_LIST_MAIL_FLOW_LINK",
  "MAIL_FLOW_KNOWN_MALICIOUS_SENDERS",
  "M365_AZURE_APP_CHOOSER",
  "PATTERN_FILTERING",
  "M365_AZURE_APP_MIGRATION",
  "MAIL_FLOW_QUARANTINE_FOLDER",
] as const;

export type FlagName = (typeof FLAG_NAMES)[number];

export type FlagsConfig = {
  [K in FlagName]: boolean;
};

declare global {
  var __FLAGS__: FlagsConfig | undefined;
}

const flagsRef = shallowRef<FlagsConfig | null>(null);

const isFlagConfig = (value: unknown): value is boolean =>
  typeof value === "boolean";

const validateFlagsConfig = (data: unknown): FlagsConfig => {
  if (!data || typeof data !== "object") {
    throw new Error("Flags config must be an object.");
  }

  const record = data as Record<string, unknown>;
  const missing = FLAG_NAMES.filter((name) => !isFlagConfig(record[name]));
  if (missing.length) {
    throw new Error(`Missing required flag definitions: ${missing.join(", ")}`);
  }

  const unknownKeys = Object.keys(record).filter(
    (key) => !FLAG_NAMES.includes(key as FlagName),
  );
  if (unknownKeys.length) {
    throw new Error(`Unknown flag names: ${unknownKeys.join(", ")}`);
  }

  return record as FlagsConfig;
};

export const loadFlagsConfig = (): FlagsConfig => {
  if (flagsRef.value) return flagsRef.value;

  const data = globalThis.__FLAGS__;
  if (!data) {
    throw new Error("Flags config is missing.");
  }
  const validated = validateFlagsConfig(data);
  flagsRef.value = validated;
  return validated;
};

export const useFlagsConfig = () => flagsRef;

export const getFlagsConfig = (): FlagsConfig => {
  if (!flagsRef.value) {
    throw new Error("Flags config not loaded.");
  }
  return flagsRef.value;
};

export const resetFlagsConfigForTests = () => {
  flagsRef.value = null;
  delete globalThis.__FLAGS__;
};
